import { useState, useRef, useEffect } from 'react'
import { Check, ChevronDown, Layers, MoreHorizontal, Pencil, Plus, Star, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import type { TableView, TableViewConfig } from '@/admin/types/table-views'

interface ViewSwitcherProps {
  views: TableView[]
  activeViewUuid: string | null
  onSelectView: (view: TableView | null) => void
  onSaveView: (name: string, config: TableViewConfig) => void
  onDeleteView: (uuid: string) => void
  onRenameView: (uuid: string, name: string) => void
  onSetOrgDefault?: (config: TableViewConfig) => void
  currentConfig: TableViewConfig
  /** The open view has changes not saved to it yet. */
  activeViewModified?: boolean
  /** Bump to open the name box from outside (OpenViewBar's "Save as new view"). */
  namingRequest?: number
  /**
   * Whether the user may rename or delete a view (their own, or an org
   * default they manage). Others' shared views get neither. Defaults to yes.
   */
  canEditView?: (view: TableView) => boolean
  canManageDefaults?: boolean
}

/**
 * The saved views of a list, as one picker (WPS-276 rework). The button names
 * the open view (or "Standard view", as on the dashboard); its menu lists the user's own views, the
 * organisation default and views shared with them, and saves the list as
 * shown as a new view. However many views someone keeps, they take one place
 * on the page, the same way the dashboard's Views menu does.
 */
export function ViewSwitcher({
  views,
  activeViewUuid,
  onSelectView,
  onSaveView,
  onDeleteView,
  onRenameView,
  onSetOrgDefault,
  currentConfig,
  activeViewModified = false,
  namingRequest = 0,
  canEditView = () => true,
  canManageDefaults,
}: ViewSwitcherProps) {
  const { t } = useTranslation()
  const [isNaming, setIsNaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [renamingUuid, setRenamingUuid] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  // Setting the org default reaches everyone, so it is confirmed first.
  const [defaultCandidate, setDefaultCandidate] = useState<TableView | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  // "Save as new view..." in the menu opens the name box at once; the menu is
  // told not to hand focus back to its button on the way out, or the box would
  // lose focus (and close) the moment it opened.
  const namingRequestedRef = useRef(false)

  // Focus input when naming or renaming
  useEffect(() => {
    if (isNaming && inputRef.current) inputRef.current.focus()
  }, [isNaming])

  useEffect(() => {
    if (renamingUuid && renameRef.current) renameRef.current.focus()
  }, [renamingUuid])

  // Set once the name box is saved or cancelled, so the blur that follows
  // (the box closing) does not act on it a second time.
  const namingSettledRef = useRef(false)

  function startNaming() {
    namingSettledRef.current = false
    setIsNaming(true)
  }

  // "Save as new view" on the bar asks for a name in the same box.
  useEffect(() => {
    if (namingRequest === 0) return
    namingSettledRef.current = false
    setIsNaming(true)
  }, [namingRequest])

  function stopNaming() {
    namingSettledRef.current = true
    setNewName('')
    setIsNaming(false)
  }

  function handleSave() {
    const name = newName.trim()
    if (!name || namingSettledRef.current) return
    onSaveView(name, currentConfig)
    stopNaming()
  }

  function handleRename(uuid: string) {
    const name = renameValue.trim()
    const view = views.find((v) => v.uuid === uuid)
    setRenamingUuid(null)
    setRenameValue('')
    // Empty name asks to delete (via a non-dismissible confirm), so clicking
    // away can't nuke a view by accident. An unchanged name just closes.
    if (!name) {
      setDeletingUuid(uuid)
    } else if (view && name !== view.name) {
      onRenameView(uuid, name)
    }
  }

  // Saving changes into the open view is OpenViewBar's job: nothing here
  // writes to a view except create, rename and delete.

  const activeView = views.find((v) => v.uuid === activeViewUuid) ?? null
  const deletingView = deletingUuid ? views.find((v) => v.uuid === deletingUuid) : null
  const ownViews = views.filter((v) => !v.is_org_default && v.is_owner !== false)
  const defaultViews = views.filter((v) => v.is_org_default)
  const sharedViews = views.filter((v) => !v.is_org_default && v.is_owner === false)
  const groups: Array<{ key: string; label: string; items: TableView[] }> = [
    { key: 'own', label: t('viewSwitcher.yourViews', 'Your views'), items: ownViews },
    {
      key: 'default',
      label: t('viewSwitcher.organisationDefault', 'Organisation default'),
      items: defaultViews,
    },
    { key: 'shared', label: t('viewSwitcher.sharedWithYou', 'Shared with you'), items: sharedViews },
  ]
  const standardLabel = t('viewSwitcher.standardView', 'Standard view')

  return (
    <div className="cv-data-table flex min-w-0 flex-wrap items-center gap-1.5">
      {activeView && renamingUuid === activeView.uuid ? (
        <Input
          ref={renameRef}
          autoComplete="off"
          aria-label={t('viewSwitcher.renameLabel', 'New name for the view')}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={() => handleRename(activeView.uuid)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRename(activeView.uuid)
            if (e.key === 'Escape') {
              setRenamingUuid(null)
              setRenameValue('')
            }
          }}
          className="cv-data-table-body h-8 w-[220px] shrink-0 rounded-[8px] border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3 text-[var(--ink)] outline-none"
        />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                data-testid="view-picker-trigger"
                className="cv-data-table-body flex h-8 max-w-[280px] cursor-pointer items-center gap-2 rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] px-3 font-normal text-[var(--ink)] transition-colors hover:border-[var(--ink-3)] hover:bg-[var(--bg-subtle)] aria-expanded:bg-[var(--bg-subtle)]"
              />
            }
          >
            <Layers className="size-3.5 shrink-0 text-[var(--ink-3)]" />
            <span className="truncate font-medium" data-testid="view-picker-current">
              {activeView ? activeView.name : standardLabel}
            </span>
            {activeView && activeViewModified && (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]"
                title={t('viewSwitcher.unsavedChanges', 'Unsaved changes')}
              />
            )}
            <ChevronDown className="size-3.5 shrink-0 text-[var(--ink-3)]" />
          </DropdownMenuTrigger>
          {/* The list scrolls; "Save as new view..." stays pinned below it, so
              it is in reach however many views someone keeps. */}
          <DropdownMenuContent
            align="start"
            className="flex max-h-[min(460px,72vh)] min-w-[260px] max-w-[340px] flex-col"
            finalFocus={() => {
              if (!namingRequestedRef.current) return true
              namingRequestedRef.current = false
              return false
            }}
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <DropdownMenuItem
                onClick={() => {
                  if (activeView) onSelectView(null)
                }}
              >
                <span className="flex-1">
                  {standardLabel}
                  <small className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
                    {t('viewSwitcher.standardListSub', 'Your own filters and columns, not a saved view')}
                  </small>
                </span>
                {activeView === null && <Check className="text-[var(--green)]" />}
              </DropdownMenuItem>
              {groups
                .filter((g) => g.items.length > 0)
                .map((g) => (
                  <DropdownMenuGroup key={g.key}>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[11px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]">
                      {g.label}
                    </DropdownMenuLabel>
                    {g.items.map((view) => {
                      const isActive = view.uuid === activeViewUuid
                      return (
                        <DropdownMenuItem
                          key={view.uuid}
                          data-active={isActive ? 'true' : undefined}
                          data-testid="view-picker-item"
                          onClick={() => {
                            if (!isActive) onSelectView(view)
                          }}
                        >
                          {view.is_org_default && <Star className="fill-current text-[var(--ink-3)]" />}
                          <span className="flex-1 truncate" title={view.name}>
                            {view.name}
                          </span>
                          {isActive && activeViewModified && (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]" />
                          )}
                          {isActive && <Check className="text-[var(--green)]" />}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuGroup>
                ))}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="shrink-0"
              onClick={() => {
                namingRequestedRef.current = true
                startNaming()
              }}
            >
              <Plus />
              {t('viewSwitcher.saveAsNewMenu', 'Save as new view...')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {activeView && renamingUuid !== activeView.uuid && (
        <OpenViewControls
          viewName={activeView.name}
          editable={canEditView(activeView)}
          onRename={() => {
            setRenamingUuid(activeView.uuid)
            setRenameValue(activeView.name)
          }}
          onDelete={() => setDeletingUuid(activeView.uuid)}
          onMakeDefault={
            canManageDefaults && onSetOrgDefault && !activeView.is_org_default
              ? () => setDefaultCandidate(activeView)
              : undefined
          }
          onClose={() => onSelectView(null)}
        />
      )}
      {isNaming && (
        <Input
          ref={inputRef}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          // Clicking away keeps a typed name as a saved view (there is no
          // separate Save button); an empty box just closes. Escape cancels.
          onBlur={() => {
            if (namingSettledRef.current) return
            if (newName.trim()) handleSave()
            else stopNaming()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSave()
            }
            if (e.key === 'Escape') stopNaming()
          }}
          // Not a form the browser should remember: its old entries
          // dropped down over the box and hid what was being typed.
          autoComplete="off"
          aria-label={t('viewSwitcher.nameLabel', 'Name for the new view')}
          placeholder={t('viewSwitcher.namePlaceholder', 'View name, then Enter')}
          className="cv-data-table-body h-8 w-[220px] shrink-0 rounded-[8px] border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3 text-[var(--ink)] outline-none"
        />
      )}
      {deletingView && (
        <ConfirmDeleteDialog
          open={!!deletingUuid}
          onOpenChange={(o) => {
            if (!o) setDeletingUuid(null)
          }}
          dismissible={false}
          title={t('viewSwitcher.deleteTitle', {
            defaultValue: 'Delete "{{name}}"?',
            name: deletingView.name,
          })}
          description={t('viewSwitcher.deleteDescription', {
            defaultValue: "This removes the saved view and can't be undone.",
          })}
          onConfirm={() => {
            onDeleteView(deletingView.uuid)
            setDeletingUuid(null)
          }}
        />
      )}
      {defaultCandidate && onSetOrgDefault && (
        <ConfirmationDialog
          open
          onOpenChange={(o) => {
            if (!o) setDefaultCandidate(null)
          }}
          title={t('viewSwitcher.setDefaultTitle', {
            defaultValue: 'Make "{{name}}" the default view?',
            name: defaultCandidate.name,
          })}
          description={t(
            'viewSwitcher.setDefaultDescription',
            'This becomes the default view for everyone in your organisation.',
          )}
          confirmLabel={t('viewSwitcher.setDefaultConfirm', 'Make default')}
          onConfirm={() => {
            onSetOrgDefault(defaultCandidate.config)
            setDefaultCandidate(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * The controls beside the open view's name: a menu holding rename, delete
 * and (for those who manage them) making it the organisation default, and an
 * x that closes the view. Close is one click; delete is two steps away (menu,
 * then confirm), so the two can no longer be mistaken for each other.
 */
function OpenViewControls({
  viewName,
  editable,
  onRename,
  onDelete,
  onMakeDefault,
  onClose,
}: {
  viewName: string
  /** The user may rename or delete this view. Others' shared views get the x only. */
  editable: boolean
  onRename: () => void
  onDelete: () => void
  onMakeDefault?: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  // Rename opens its box at once; the menu is told not to hand focus back to
  // its button, or the rename box would lose focus (and close) as it opened.
  const renameRequestedRef = useRef(false)
  const iconButton =
    'size-7 cursor-pointer rounded-[6px] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)] aria-expanded:bg-[var(--bg-subtle)]'
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {editable && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                className={iconButton}
                aria-label={t('viewSwitcher.moreActions', {
                  defaultValue: 'More actions for "{{name}}"',
                  name: viewName,
                })}
                title={t('viewSwitcher.moreActionsTitle', 'Rename or delete')}
              />
            }
          >
            <MoreHorizontal className="size-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[200px]"
            finalFocus={() => {
              if (!renameRequestedRef.current) return true
              renameRequestedRef.current = false
              return false
            }}
          >
            <DropdownMenuItem
              onClick={() => {
                renameRequestedRef.current = true
                onRename()
              }}
            >
              <Pencil className="size-3.5" />
              {t('viewSwitcher.rename', 'Rename')}
            </DropdownMenuItem>
            {onMakeDefault && (
              <DropdownMenuItem onClick={onMakeDefault}>
                <Star className="size-3.5" />
                {t('viewSwitcher.makeOrgDefault', 'Make the default for everyone')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={onDelete}>
              <Trash2 className="size-3.5" />
              {t('viewSwitcher.deleteView', 'Delete view...')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={onClose}
        className={iconButton}
        aria-label={t('viewSwitcher.closeViewNamed', {
          defaultValue: 'Close "{{name}}"',
          name: viewName,
        })}
        title={t('viewSwitcher.closeView', 'Close view')}
      >
        <X className="size-3.5" />
      </Button>
    </span>
  )
}

/**
 * What can be done with the list's changes, beside the open view's name on a
 * list (and under the dashboard's header). Nothing saves itself (WPS-276
 * rework): a change shows here as unsaved, and the user chooses to Save it
 * into the view, Save it as a new view (leaving the one they started from as
 * it was), or Discard it. While a view is open, Save always sits in the same
 * place and lights up once there is something to save. With no view open, a
 * list that has been set up offers Save as new view, so people set the list up
 * first and name it last. One component on every surface with saved views.
 */
export function OpenViewBar({
  viewName,
  modified,
  canSave,
  seenByOthers = false,
  isOrgDefault = false,
  saving = false,
  showWhenClean = true,
  nameShown = false,
  noViewNotice,
  discardLabel,
  onSave,
  onSaveAsNew,
  onDiscard,
  onClose,
}: {
  /** The open view's name; null when no saved view is open (a changed standard view). */
  viewName: string | null
  /** The list differs from the view as saved (or, with no view, from a plain list). */
  modified: boolean
  /** The user may save into this view (their own, or a default they manage). */
  canSave: boolean
  /** Others see this view, so saving it is confirmed first. */
  seenByOthers?: boolean
  isOrgDefault?: boolean
  saving?: boolean
  /** Show the bar while nothing has changed (off where the dashboard header names the view). */
  showWhenClean?: boolean
  /** The view's name is shown beside the bar (the list picker), so the bar does not repeat it. */
  nameShown?: boolean
  /** The line shown when no view is open and the list has changed. */
  noViewNotice?: string
  /** The Discard button's label, where "discard" means something more specific. */
  discardLabel?: string
  onSave: () => void
  onSaveAsNew: () => void
  onDiscard: () => void
  onClose?: () => void
}) {
  const { t } = useTranslation()
  const [confirmingSave, setConfirmingSave] = useState(false)
  if (!modified && (!showWhenClean || viewName === null)) return null

  const message =
    viewName === null
      ? (noViewNotice ?? t('viewSwitcher.unsavedStandardNotice', 'You changed the standard view.'))
      : nameShown
        ? !modified
          ? canSave
            ? t('viewSwitcher.cleanHint', 'Changes are not saved until you press Save.')
            : t(
                'viewSwitcher.sharedCleanHint',
                'Shared with you. To keep changes, save them as a new view.',
              )
          : canSave
            ? t('viewSwitcher.unsavedShort', 'Unsaved changes')
            : t(
                'viewSwitcher.unsavedSharedShort',
                'Unsaved changes. This view is shared with you, so save them as a new view.',
              )
        : !modified
          ? canSave
            ? t('viewSwitcher.viewingNotice', {
                defaultValue:
                  'Viewing "{{name}}". Changes you make are not saved until you press Save.',
                name: viewName,
              })
            : t('viewSwitcher.viewingSharedNotice', {
                defaultValue:
                  'Viewing "{{name}}", shared with you. To keep changes, save them as a new view.',
                name: viewName,
              })
          : canSave
            ? t('viewSwitcher.unsavedNotice', {
                defaultValue: 'Unsaved changes to "{{name}}".',
                name: viewName,
              })
            : t('viewSwitcher.unsavedSharedNotice', {
                defaultValue:
                  'Unsaved changes to "{{name}}". It is shared with you, so save them as a new view.',
                name: viewName,
              })
  // Save lives in one place while a view is open, quiet until there is
  // something to save, so people always know where it is.
  const showSave = canSave && viewName !== null

  return (
    <div
      data-testid="open-view-bar"
      className={`cv-data-table-body flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5 text-[var(--ink-2)] ${
        nameShown ? 'flex-1' : ''
      }`}
    >
      {modified && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]" />}
      <span className={modified ? '' : 'text-[var(--ink-3)]'}>{message}</span>
      {nameShown && <span className="flex-1" />}
      <span className="flex flex-wrap items-center gap-1.5">
        {modified && (
          <Button
            size="xs"
            variant="ghost"
            onClick={onDiscard}
            className="cursor-pointer text-[var(--ink-2)]"
          >
            {discardLabel ?? t('viewSwitcher.discardChanges', 'Discard changes')}
          </Button>
        )}
        {(modified || viewName !== null) && (
          <Button size="xs" variant="outline" onClick={onSaveAsNew} className="cursor-pointer">
            {t('viewSwitcher.saveAsNew', 'Save as new view')}
          </Button>
        )}
        {showSave && (
          <Button
            size="xs"
            variant={modified ? 'default' : 'outline'}
            // Nothing to save yet: Save stays where it is, quiet, and says so.
            disabled={saving || !modified}
            title={modified ? undefined : t('viewSwitcher.nothingToSave', 'Nothing to save yet')}
            onClick={() => (seenByOthers ? setConfirmingSave(true) : onSave())}
            className="cursor-pointer"
          >
            {t('viewSwitcher.save', 'Save')}
          </Button>
        )}
      </span>
      {onClose && (
        <Button
          variant="link"
          onClick={onClose}
          className="h-auto cursor-pointer p-0 text-[length:var(--cv-data-table-body-size)] font-medium text-[var(--green)]"
        >
          {t('viewSwitcher.closeView', 'Close view')}
        </Button>
      )}
      {showSave && seenByOthers && (
        <ConfirmationDialog
          open={confirmingSave}
          onOpenChange={setConfirmingSave}
          title={t('viewSwitcher.saveSharedTitle', {
            defaultValue: 'Save changes to "{{name}}"?',
            name: viewName,
          })}
          description={
            isOrgDefault
              ? t(
                  'viewSwitcher.saveOrgDefaultDescription',
                  'This changes it for everyone in your organisation.',
                )
              : t(
                  'viewSwitcher.saveSharedDescription',
                  'This changes it for everyone it is shared with.',
                )
          }
          confirmLabel={t('viewSwitcher.saveForEveryone', 'Save for everyone')}
          onConfirm={() => {
            onSave()
            setConfirmingSave(false)
          }}
        />
      )}
    </div>
  )
}

/**
 * Asked when the user leaves an open view that has unsaved changes (closing
 * it, opening another, or going back to all): save them, discard them, or
 * stay. Leaving no longer drops a careful setup without a word.
 */
export function UnsavedViewDialog({
  open,
  viewName,
  canSave,
  seenByOthers = false,
  onSave,
  onSaveAsNew,
  onDiscard,
  onCancel,
}: {
  open: boolean
  viewName: string
  canSave: boolean
  seenByOthers?: boolean
  onSave: () => void
  onSaveAsNew: () => void
  onDiscard: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel()
      }}
    >
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {t('viewSwitcher.leaveTitle', {
              defaultValue: 'Save changes to "{{name}}"?',
              name: viewName,
            })}
          </DialogTitle>
          <DialogDescription>
            {canSave
              ? seenByOthers
                ? t(
                    'viewSwitcher.leaveSharedDescription',
                    'You changed this view. Saving changes it for everyone who sees it.',
                  )
                : t(
                    'viewSwitcher.leaveDescription',
                    'You changed this view. Save the changes, or discard them and move on.',
                  )
              : t(
                  'viewSwitcher.leaveReadOnlyDescription',
                  'You changed a view shared with you. Keep the changes as a new view, or discard them.',
                )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="cursor-pointer">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="ghost" onClick={onDiscard} className="cursor-pointer">
            {t('viewSwitcher.discardChanges', 'Discard changes')}
          </Button>
          {canSave ? (
            <Button onClick={onSave} className="cursor-pointer">
              {seenByOthers
                ? t('viewSwitcher.saveForEveryone', 'Save for everyone')
                : t('viewSwitcher.save', 'Save')}
            </Button>
          ) : (
            <Button onClick={onSaveAsNew} className="cursor-pointer">
              {t('viewSwitcher.saveAsNew', 'Save as new view')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
